'use client';

import { useRef } from 'react';
import { cn } from '@fshr/shared-react';
import Button from '../../button';
import { IconExpand } from '../../icon';
import TableScrollTrack from '../layout/table-scroll-track';
import useTableColumnLayout from '../layout/use-table-column-layout';
import SortableTableBody from './sortable-table-body';
import SortableTableHeader from './sortable-table-header';
import type { SortableTableSectionProps } from './types';

export type { SortableTableColumnMeta, SortableTableSectionProps } from './types';

export default function SortableTableSection({
	tableId,
	layoutKey,
	columnMeta,
	processedRows,
	visibleIndices,
	stickyRowHeader,
	scrollBlockClassName,
	contentClassName,
	scrollTrackClassName,
	noResultsText,
	showViewMore,
	showingAll,
	onToggleShowingAll,
	viewMoreText,
	viewLessText,
	activeSortCol,
	activeSortDir,
	onSort,
}: SortableTableSectionProps) {
	const tableRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const trackRef = useRef<HTMLDivElement>(null);

	const {
		columnWidths,
		layoutMode,
		isHorizontalOverflow,
		isCompressed,
		isStickyScrolled,
		thumbStyle,
	} = useTableColumnLayout(tableRef, scrollRef, trackRef, layoutKey, stickyRowHeader);

	const hasColumnWidths = columnWidths?.some(width => width != null) ?? false;

	const rowClassName = cn(
		'flex flex-nowrap border-t border-border-subtle',
		layoutMode === 'scroll' ? 'w-max' : isHorizontalOverflow ? 'w-max min-w-full' : 'w-full'
	);

	const cellLayoutContext = {
		columnWidths,
		layoutMode,
		isHorizontalOverflow,
		isCompressed,
		hasColumnWidths,
		stickyRowHeader,
		isStickyScrolled,
		rowClassName,
	};

	const hasNoResults = visibleIndices.length === 0;
	const noResultsId = `${tableId}-no-results`;

	return (
		<div
			className={cn(
				'relative flex min-w-0 flex-col spacing-stack-l',
				isHorizontalOverflow && 'max-w-full overflow-x-hidden'
			)}
		>
			<div
				ref={scrollRef}
				className={cn(
					'min-w-0 w-full touch-pan-x touch-pan-y overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
					scrollBlockClassName
				)}
			>
				<div
					ref={tableRef}
					id={tableId}
					role="table"
					aria-describedby={hasNoResults ? noResultsId : undefined}
					className={cn(
						'box-border flex flex-col',
						layoutMode === 'fill' ? 'w-full min-w-full' : 'min-w-min',
						'data-[intrinsic-measure=true]:w-max data-[intrinsic-measure=true]:min-w-full',
						'data-[intrinsic-measure=true]:[&_[data-sortable-cell]]:shrink-0',
						isCompressed &&
							isHorizontalOverflow &&
							'[&_[data-sortable-row]:not([data-row-role=header])_[data-sortable-cell]:not(:first-child)_*]:min-w-0 [&_[data-sortable-row]:not([data-row-role=header])_[data-sortable-cell]:not(:first-child)_*]:max-w-full [&_[data-sortable-row]:not([data-row-role=header])_[data-sortable-cell]:not(:first-child)_*]:overflow-hidden'
					)}
				>
					<div data-sortable-content className={cn('box-border flex flex-col', contentClassName)}>
						<SortableTableHeader
							{...cellLayoutContext}
							columnMeta={columnMeta}
							activeSortCol={activeSortCol}
							activeSortDir={activeSortDir}
							onSort={onSort}
						/>
						<SortableTableBody
							{...cellLayoutContext}
							processedRows={processedRows}
							visibleIndices={visibleIndices}
						/>
					</div>
				</div>
			</div>

			{hasNoResults ? (
				<p
					id={noResultsId}
					role="status"
					aria-live="polite"
					className="text-center text-subtitle1 text-text-heading spacing-stack-l"
				>
					{noResultsText}
				</p>
			) : null}

			<div
				ref={trackRef}
				className={cn(
					!isHorizontalOverflow && 'pointer-events-none absolute h-0 w-full overflow-hidden'
				)}
			>
				<TableScrollTrack
					className={scrollTrackClassName}
					visible={isHorizontalOverflow}
					thumbStyle={thumbStyle}
				/>
			</div>

			{showViewMore ? (
				<div className="flex justify-center">
					<Button
						intent="outline"
						size="small"
						className="min-w-[125px]"
						icon={<IconExpand width={11} height={6} className={cn(showingAll && 'rotate-180')} />}
						onClick={onToggleShowingAll}
					>
						{showingAll ? viewLessText : viewMoreText}
					</Button>
				</div>
			) : null}
		</div>
	);
}
